import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import { CONTRACT_ACTIONS } from '../constants';
import type { IContractPageBody } from '../sharedTypes';
import type { ILoadContractRequestPayload, ILoadContractSuccessPayload, IViewAgencyContractState } from './types';

const initState: IViewAgencyContractState = {
  requestedId: null,
  contract: null,
  activeTab: CONTRACT_ACTIONS.CURRENT_SHOW,
  // The load is dispatched from an effect, which runs after the first paint. Starting at `false`
  // would render one frame with no contract and no request, which reads as a failure.
  loading: true,
  refreshing: false,
  error: false,
  isLegacy: false,
  notFound: false,
  message: null,
};

// The same contract's body stays on screen while another amendment loads: dropping it would spin the page
// and remount every agency section. Another contract's body — left by an editor — is dropped unpainted.
function onLoadRequest(
  state: IViewAgencyContractState,
  { payload }: { payload: ILoadContractRequestPayload },
): IViewAgencyContractState {
  const sameContract = state.contract?.id === payload.id;

  return {
    ...state,
    requestedId: payload.id,
    contract: sameContract ? state.contract : null,
    loading: !sameContract,
    refreshing: sameContract,
    error: false,
    notFound: false,
    isLegacy: false,
    message: null,
  };
}

function onLoadSuccess(
  state: IViewAgencyContractState,
  { payload }: { payload: ILoadContractSuccessPayload },
): IViewAgencyContractState {
  return {
    ...state,
    loading: false,
    refreshing: false,
    error: false,
    message: null,
    contract: payload.contract,
    activeTab: payload.tab,
  };
}

function onLoadLegacy(state: IViewAgencyContractState): IViewAgencyContractState {
  return { ...state, loading: false, refreshing: false, isLegacy: true };
}

// A failed refresh drops the stale body too: the error panel is what the page has to say now.
function onLoadError(
  state: IViewAgencyContractState,
  { payload }: { payload: { notFound: boolean; message: string | null } },
): IViewAgencyContractState {
  return {
    ...state,
    loading: false,
    refreshing: false,
    error: true,
    notFound: payload.notFound,
    message: payload.message,
    contract: null,
  };
}

function onSeed(
  state: IViewAgencyContractState,
  { payload }: { payload: { contract: IContractPageBody } },
): IViewAgencyContractState {
  return {
    ...state,
    loading: false,
    refreshing: false,
    error: false,
    notFound: false,
    isLegacy: false,
    message: null,
    requestedId: payload.contract.id,
    contract: payload.contract,
    activeTab: CONTRACT_ACTIONS.CURRENT_SHOW,
  };
}

export default createReducer(initState, {
  [actionTypes.LOAD_CONTRACT_REQUEST]: onLoadRequest,
  [actionTypes.LOAD_CONTRACT_SUCCESS]: onLoadSuccess,
  [actionTypes.LOAD_CONTRACT_LEGACY]: onLoadLegacy,
  [actionTypes.LOAD_CONTRACT_ERROR]: onLoadError,
  [actionTypes.SEED_CONTRACT]: onSeed,
  [actionTypes.CLEAR_STORE]: () => initState,
});
